import { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle2, ListTodo, AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePublicTodoList, useCompletedTimeline } from '../hooks';
import { SectionCard, TodoItemRow, CompletedTimeline } from '../components';
import { TodoListProvider } from '../context';
import type { TodoListContextValue } from '../context';

type TabValue = 'upcoming' | 'completed';

export function PublicListPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const isCompletedView = location.pathname.endsWith('/completed');
  const activeTab: TabValue = isCompletedView ? 'completed' : 'upcoming';

  const { list, loading, error } = usePublicTodoList(slug!);
  const {
    items: completedItems,
    loading: completedLoading,
    loadMore,
    hasMore,
  } = useCompletedTimeline({ slug: slug! });

  const topLevelSections = useMemo(() => {
    if (!list) return [];
    return list.sections
      .filter((s) => s.parentSectionId === null)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [list]);

  const unsortedItems = useMemo(() => {
    if (!list) return [];
    return list.items.filter((item) => item.sectionId === null && item.status !== 'completed');
  }, [list]);

  const publicContextValue: TodoListContextValue = useMemo(
    () => ({
      isOwner: false,
      collaborators: [],
      itemAssignments: [],
      sectionAssignments: [],
      currentUserId: undefined,
      visibleSectionIds: undefined,
    }),
    [],
  );

  const handleTabChange = (tab: TabValue) => {
    if (tab === 'completed') {
      navigate(`/a/arche_todo_lists/public/${slug}/completed`);
    } else {
      navigate(`/a/arche_todo_lists/public/${slug}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Skeleton className="h-10 w-64 mb-3" />
          <Skeleton className="h-5 w-96 mb-8" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              {error?.includes('not found') || !error ? 'Not Found' : 'Error'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {error || 'This todo list could not be found or is no longer public.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <TodoListProvider value={publicContextValue}>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="max-w-3xl mx-auto px-4 py-6">
            <div className="flex items-start gap-3 mb-2">
              <ListTodo className="h-7 w-7 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h1 className="text-2xl font-bold">{list.name}</h1>
                {list.description && (
                  <p className="text-muted-foreground mt-1">{list.description}</p>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Tab navigation */}
        <div className="border-b">
          <div className="max-w-3xl mx-auto px-4">
            <nav className="flex gap-6" aria-label="View mode">
              <button
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'upcoming'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => handleTabChange('upcoming')}
              >
                Upcoming
              </button>
              <button
                className={`py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                  activeTab === 'completed'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => handleTabChange('completed')}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Completed
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <main className="flex-1">
          <div className="max-w-3xl mx-auto px-4 py-6">
            {activeTab === 'upcoming' ? (
              <div className="space-y-4">
                {topLevelSections.map((section) => (
                  <SectionCard
                    key={section.sectionId}
                    section={section}
                    sections={list.sections}
                    items={list.items.filter((i) => i.status !== 'completed')}
                  />
                ))}

                {unsortedItems.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        Unsorted
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      {unsortedItems.map((item) => (
                        <TodoItemRow key={item.itemId} item={item} />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {topLevelSections.length === 0 && unsortedItems.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>No items to show.</p>
                  </div>
                )}
              </div>
            ) : completedLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-md" />
                ))}
              </div>
            ) : completedItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No completed items yet</p>
              </div>
            ) : (
              <CompletedTimeline
                items={completedItems}
                sections={list?.sections ?? []}
                onLoadMore={loadMore}
                hasMore={hasMore}
                loading={completedLoading}
              />
            )}
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t py-6">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <a
              href="/"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Powered by Fias
            </a>
          </div>
        </footer>
      </div>
    </TodoListProvider>
  );
}
